import { createSlice } from '@reduxjs/toolkit';
import {
  getBasketsbyDateRangeThunk,
  getInventoryDataForSubstitutionThunk,
  getOrderDetailsByIdThunk,
} from './OrderDetailsActions';
import { IBasket } from '../../OrderEntryForm/OrderEntrySlice';
import { holdInventoryDataThunk } from '../../../pages/SwapBaskets/SwapBasketsActions';
import { getObjectFromStorage, setObjectInStorage } from '../../../services/storageService';
import { ArrayUtil } from 'helper-util';
import { addNewRowStatus } from './OrderDetailsHelper';

export interface IOrderDetails {
  clientId: string;
  counterParty: string;
  groupId: string;
  id: string;
  maturityDate: string;
  settlementDate: string;
  status: string;
  swapAttributes: string;
  swapVersion: string;
  swapId: string;
  tenantId: string;
  terminationDate: string;
  timestamp: string;
  tradeDate: string;
  swapName: string;
  currentBasket: IBasket;
}

export interface ISecurity {
  ticker: string;
  securityName: string;
  cusip: string;
  isin: string;
  sedol: string;
  issueCurrency: string;
  bnySecurityId: string;
}
export interface IPositionSecurity {
  asOfTimestamp: string;
  assetType: string;
  availableQuantity: number;
  bnySecurityId: string;
  fundId: string;
  fxRate: number;
  indicativeRateFee: number;
  indicativeRateRebate: number;
  inputCurrency: string;
  location: string;
  marketValueAvailable: number;
  onLoanMarketValue: number;
  onLoanQuantity: number;
  restricted: false;
  securityCusip: string;
  securityIsin: string;
  securityIssueCountry: string;
  securityMarketPrice: number;
  securityName: string;
  securitySedol: string;
  securityTicker: string;
  shortCode: string;
  specialness: string;
  totalMarketValue: number;
}

export interface OrderDetailsData {
  orderDetails: IOrderDetails;
  baskets: IBasket[];
  basket: IBasket;
  loading: boolean;
  basketVersion: number;
  swapVersion: number;
  holdObject: any;
  securityPositionMaster: IPositionSecurity[];
}

const initialState: OrderDetailsData = {
  orderDetails: {} as IOrderDetails,
  basket: {} as IBasket,
  baskets: [],
  loading: false,
  basketVersion: 1,
  swapVersion: 1,
  holdObject: {},
  securityPositionMaster: [],
};

const orderDetailsSlice = createSlice({
  name: 'Order Details',
  initialState,
  reducers: {
    deleteCancelledRow: (state, action) => {
      state.basket = {
        ...state.basket,
        rollupItemDetails: {
          ...state.basket.rollupItemDetails,
          basketItems: state.basket.rollupItemDetails.basketItems.filter(
            (b) => b.basketItemId !== action.payload.id,
          ),
        },
      };
    },
    resetHoldObject: (state) => {
      state.holdObject = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrderDetailsByIdThunk.pending, (state) => {
        state.loading = true;
        state.orderDetails = initialState.orderDetails;
        state.basket = initialState.basket;
      })
      .addCase(getOrderDetailsByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.orderDetails = action.payload?.data?.swap || {};
        const currentBasket = action.payload?.data?.swap?.currentBasket;
        state.basket = {
          ...action.payload?.data?.swap?.currentBasket,
          rollupItemDetails: {
            ...currentBasket.rollupItemDetails,
            basketItems:
              currentBasket?.rollupItemDetails?.basketItems?.map((bsktItm) => {
                return {
                  ...bsktItm,
                  hasBelongToCurrentBasket:
                    currentBasket.status === 'DRAFT_FOR_EDIT' &&
                    bsktItm.originalBasketId === currentBasket.id,
                };
              }) || [],
          },
        };
        // state.basket = action.payload?.data?.swap?.baskets
        //   ? (action.payload?.data?.swap?.baskets.find((b) => b.version === state.basketVersion) ??
        //     action.payload?.data?.swap?.baskets[0])
        //   : ({} as IBasket);
      })
      .addCase(getOrderDetailsByIdThunk.rejected, (state) => {
        state.loading = false;
        state.orderDetails = initialState.orderDetails;
        state.basket = initialState.basket;
      })
      .addCase(holdInventoryDataThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(holdInventoryDataThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.holdObject = action.payload;

        const holdIds = getObjectFromStorage('holdIds') || [];
        const fromSubstitution = action.meta.arg?.params?.fromSubstitution;

        if (action.payload?.basketCreated) {
          setObjectInStorage(
            'holdIds',
            holdIds.concat(
              !ArrayUtil.isEmpty(action.payload?.results)
                ? action.payload?.results
                    ?.filter((rslt) => !!rslt.holdId)
                    ?.map((rslt) => rslt.holdId)
                : [],
            ),
          );
        }
        let updatedItems = action.payload?.createdBasket?.items;
        if (fromSubstitution) {
          updatedItems =
            action.payload?.createdBasket?.items?.map((item) => {
              const hasNotBelongToCurrentBasket =
                action.payload?.createdBasket?.status === 'DRAFT_FOR_EDIT' &&
                item.substitutionQuantity !== 0 &&
                item.originalBasketId !== action.payload?.createdBasket?.id;
              return {
                ...item,
                substitutionQuantity: hasNotBelongToCurrentBasket
                  ? (item.quantity ?? 0)
                  : (item.substitutionQuantity ?? 0),
                quantity: hasNotBelongToCurrentBasket
                  ? (item.substitutionQuantity ?? 0)
                  : (item.quantity ?? 0),
              };
            }) || [];
        }

        const items = action.payload?.createdBasket
          ? (updatedItems ?? [])
          : (state.basket.rollupItemDetails?.basketItems ?? []);

        state.basket = {
          ...state.basket,
          id: action.payload?.createdBasket?.id,
          version: action.payload?.createdBasket?.version,
          rollupItemDetails: {
            ...state.basket?.rollupItemDetails,
            basketItems: addNewRowStatus(
              items,
              action.payload?.results?.map((rslt) => rslt.holdId) || [],
            ),
          },
        };
      })
      .addCase(holdInventoryDataThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getInventoryDataForSubstitutionThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getInventoryDataForSubstitutionThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.securityPositionMaster = action.payload.items;
      })
      .addCase(getInventoryDataForSubstitutionThunk.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getBasketsbyDateRangeThunk.pending, (state) => {
        state.loading = true;
        state.baskets = [];
      })
      .addCase(getBasketsbyDateRangeThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.baskets = action.payload?.data?.baskets;
      })
      .addCase(getBasketsbyDateRangeThunk.rejected, (state) => {
        state.loading = false;
        state.baskets = [];
      });
  },
});

export const { deleteCancelledRow, resetHoldObject } = orderDetailsSlice.actions;
export default orderDetailsSlice.reducer;
